import React from 'react';
import TokenWarning from './TokenWarning';

export type PageKey = 'dashboard' | 'watchlist' | 'push' | 'creatives' | 'adagent' | 'analyse' | 'settings' | 'account';

interface Props {
  user: { email: string; name: string };
  onLogout: () => void;
  activePage: PageKey;
  onNavigate: (page: PageKey) => void;
  children: React.ReactNode;
}

const NAV_ITEMS: { key: PageKey; label: string; icon: React.ReactElement }[] = [
  { key: 'dashboard', label: 'DASHBOARD', icon: <GridIcon /> },
  { key: 'watchlist', label: 'Watchlist', icon: <WatchlistIcon /> },
  { key: 'push', label: 'Kampagne pushen', icon: <PushIcon /> },
  { key: 'creatives', label: 'Creatives', icon: <CreativesIcon /> },
  { key: 'adagent', label: 'Ad-Test-Agent', icon: <AdAgentIcon /> },
  { key: 'analyse', label: 'Analyse', icon: <AnalyseIcon /> },
  { key: 'settings', label: 'Einstellungen', icon: <SettingsIcon /> },
];

export default function Layout({ user, onLogout, activePage, onNavigate, children }: Props) {
  const activeNav = activePage === 'account' ? 'dashboard' : activePage;

  return (
    <div style={s.layout}>
      <aside style={s.sidebar}>
        <div style={s.sidebarTop}>
          <div style={s.logoRow}>
            <div style={s.logoMark}>PS</div>
            <div>
              <div style={s.logoTitle}>PS-Brain</div>
              <div style={s.logoSub}>Performing Systems</div>
            </div>
          </div>
          <nav style={s.nav}>
            {NAV_ITEMS.map(({ key, label, icon }) => (
              <div
                key={key}
                onClick={() => onNavigate(key)}
                style={{ ...s.navItem, ...(activeNav === key ? s.navActive : {}) }}
              >
                <span style={s.navIcon}>{icon}</span>
                <span style={key === 'dashboard' ? s.navLabelCaps : {}}>{label}</span>
              </div>
            ))}
          </nav>
        </div>
        <div style={s.sidebarFooter}>
          <div style={s.avatar}>{user.email[0].toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={s.userName}>{user.name}</div>
            <div style={s.userEmail}>{user.email}</div>
          </div>
          <button onClick={onLogout} style={s.logoutBtn} title="Abmelden">
            <LogoutIcon />
          </button>
        </div>
      </aside>
      <main style={s.main}>
        <TokenWarning />
        {children}
      </main>
    </div>
  );
}

function GridIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>;
}
function WatchlistIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>;
}
function PushIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
}
function CreativesIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
}
function AdAgentIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="7" width="16" height="12" rx="2"/><path d="M12 3v4"/><circle cx="9" cy="13" r="1"/><circle cx="15" cy="13" r="1"/></svg>;
}
function AnalyseIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
}
function SettingsIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;
}
function LogoutIcon() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
}

const s: Record<string, React.CSSProperties> = {
  layout: { display: 'flex', minHeight: '100vh', background: '#f5f5f7' },
  sidebar: {
    width: 240, background: '#ffffff', borderRight: '1px solid #e8e8ed',
    display: 'flex', flexDirection: 'column', position: 'fixed',
    top: 0, left: 0, height: '100vh', zIndex: 10,
  },
  sidebarTop: { flex: 1, padding: '28px 16px 16px', overflowY: 'auto' },
  logoRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 36, padding: '0 6px' },
  logoMark: {
    width: 40, height: 40, borderRadius: 10, background: '#1a3a5c',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 14, letterSpacing: '0.5px', flexShrink: 0,
  },
  logoTitle: { fontSize: 16, fontWeight: 800, color: '#1d1d1f', letterSpacing: '-0.3px' },
  logoSub: { fontSize: 11, color: '#aeaeb2', marginTop: 1 },
  nav: { display: 'flex', flexDirection: 'column', gap: 2 },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
    borderRadius: 8, color: '#6e6e73', fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
    transition: 'background 0.12s, color 0.12s',
  },
  navActive: { background: 'rgba(26,58,92,0.08)', color: '#1a3a5c', fontWeight: 600 },
  navIcon: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  navLabelCaps: { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em' },
  sidebarFooter: {
    padding: '14px 18px', borderTop: '1px solid #e8e8ed',
    display: 'flex', alignItems: 'center', gap: 10,
  },
  avatar: {
    width: 32, height: 32, borderRadius: '50%', background: '#1a3a5c',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 13, flexShrink: 0,
  },
  userName: { fontSize: 13, fontWeight: 600, color: '#1d1d1f', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  userEmail: { fontSize: 11, color: '#aeaeb2', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  logoutBtn: { background: 'transparent', border: 'none', color: '#aeaeb2', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', flexShrink: 0 },
  main: { marginLeft: 240, flex: 1, minHeight: '100vh' },
};
